import React, { FC, useCallback, useEffect, useState } from 'react';
import { TwButton, TwButtonType } from '../TwButtons/TwButtons';
import TwField, { StyledTextfieldLabel, TwInputTypes } from '../TwFields/TwFields';
import Stack from '@mui/material/Stack';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TwDialog from '../TwDialog/TwDialog';
import Box from '@mui/material/Box';
import TwRadioButton from '../TwRadioButtons/TwRadioButtons';
import Typography from '@mui/material/Typography';
import './TwEmptyMiles.scss';
import TwAutocomplete, {
  RenderOption,
  TwAutocompleteOption
} from '../TwAutocomplete/TwAutocomplete';
import TwLocationsDotsMenu from '../TwLocationsMenu/TwLocationsDotsMenu';
import useTwLocation from '../../hooks/useTwLocation';
import CreateLocation from '../../pages/Locations/CreateLocation/CreateLocation';
import EditLocation from '../../pages/Locations/EditLocation/EditLocation';
import { getMapsSearchUrl } from '../../utils/mapUtils';
import { LoadLocation } from '../../redux/api/load/load.model';
import useFeatureFlags from '../../providers/FeatureFlags/FeatureFlags.provider';
import useNotifications from '../../providers/Notifications/Notifications.provider';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { SliceStop, updateEmptyMilesSegment } from '../../redux/features/orders/ordersSlice';
import { useGetLastKnownLocationMutation } from '../../redux/api/equipment/equipmentApi';
import { format } from 'date-fns';
import LoadsService from '../../providers/Loads/Loads.service';
import { updateTripEmptyMiles } from '../../redux/features/trip/tripSlice';
import { EntityType } from '../../redux/Global.model';
import useAuth from '../../providers/Auth/Auth.provider';
import { Characters } from '../../hooks/useAllowCharacters';
import { useAllowCharacters } from '../../hooks/useAllowCharacters';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TwTooltip from '../TwTooltip/TwTooltip';
import { getTooltipText } from '../../utils/mileage/mileageUtils';
import { useGetTripByIdQuery } from '../../redux/api/trips/tripApi';
import { useGetTenantRoutingSettingsQuery } from '../../redux/api/tenant/tenantApi';
import { PROVIDER } from '../../providers/User/User.model';

interface TwEmptyMilesProps {
  firstStop: SliceStop | null;
  truck: TwAutocompleteOption | null;
  initialEmptyMiles: number | null;
  initialAutoCalculate: boolean;
  initialSource: PROVIDER | null;
  segmentId?: string;
  segmentUid?: string;
  entity: EntityType;
}

const TwEmptyMiles: FC<TwEmptyMilesProps> = ({
  firstStop,
  truck,
  initialEmptyMiles,
  initialAutoCalculate,
  initialSource,
  segmentId = '',
  segmentUid,
  entity
}): JSX.Element => {
  const {
    data: { userData }
  } = useAuth();
  const enableLtl = userData?.tenant?.ltl_enabled || false;
  const { addNotification } = useNotifications();
  const dispatch = useAppDispatch();
  const { enableAddressLocationLink, enablePcMiler } = useFeatureFlags();

  const { duplicate_load } = useAppSelector((state) => state.orders);
  const [emptyMiles, setEmptyMiles] = useState<number | undefined>(undefined);
  const [tempEmptyMiles, setTempEmptyMiles] = useState<number | undefined>(undefined);
  const [openCalculateEmptyMiles, setOpenCalculateEmptyMiles] = useState<boolean>(false);
  const [locations, setLocations] = useState<TwAutocompleteOption[]>([]);
  const [lastKnownLocation, setLastKnownLocation] = useState<TwAutocompleteOption | null>(null);
  const [isCreateLocationOpen, setIsCreateLocationOpen] = useState<boolean>(false);
  const [isEditLocationOpen, setIsEditLocationOpen] = useState<boolean>(false);
  const [originalLocation, setOriginalLocation] = useState<TwAutocompleteOption | null>(null);
  const [isAutoCalculate, setIsAutoCalculate] = useState<boolean>(false);
  const [tooltip, setTooltip] = useState<string>('');

  const { locations: twLocations, fetchLocations } = useTwLocation(true);
  const isAutoCalculateMileageDisabled =
    (userData?.tenantSettings && userData.tenantSettings['disable-auto-calculate-mileage']) ||
    false;

  const { data: routingSettings } = useGetTenantRoutingSettingsQuery(null);
  const [provider, setProvider] = useState<PROVIDER>(
    !enablePcMiler ? PROVIDER.GOOGLE : routingSettings?.provider ?? PROVIDER.GOOGLE
  );
  const [getLastKnownLocation] = useGetLastKnownLocationMutation();
  const handleKeyDownCharacters = useAllowCharacters(Characters.float);

  const getLastLocation = useCallback(async () => {
    if (truck && firstStop) {
      getLastKnownLocation({
        truck_id: truck.id,
        segment_id: segmentId && !duplicate_load ? segmentId : undefined,
        start_date: firstStop.provider_date ? new Date(firstStop.provider_date) : new Date(),
        start_time: firstStop.provider_time_from
          ? format(new Date(firstStop.provider_time_from), 'HH:mm:ss')
          : format(new Date(), 'HH:mm:ss')
      })
        .unwrap()
        .then((res) => {
          const lastLocation: TwAutocompleteOption = {
            id: res.id || '',
            label: res.name || '',
            value: res
          };
          setLastKnownLocation(lastLocation);
          setOriginalLocation(lastLocation);
        })
        .catch((err) => {
          setTempEmptyMiles(undefined);
          addNotification({
            message:
              err.data.message || 'Error while getting last known location, please try again',
            variant: 'error'
          });
        });
    }
  }, [truck, firstStop]);

  const getEmptyMiles = useCallback(async () => {
    if (lastKnownLocation && firstStop) {
      const last_location = lastKnownLocation.value as LoadLocation;
      LoadsService.getEstimatedMilage(firstStop.provider_location?.id || '', last_location.id)
        .then((res) => {
          setTempEmptyMiles(res.milage ? Number(res.milage) : 0);
        })
        .catch((err) => {
          setTempEmptyMiles(undefined);
          addNotification({
            message: err.message || 'Error while getting empty miles, please try again',
            variant: 'error'
          });
        });
    }
  }, [lastKnownLocation, firstStop]);

  useEffect(() => {
    if (initialEmptyMiles === undefined || !initialSource) return;

    setTooltip(getTooltipText('Empty miles', initialSource, initialAutoCalculate));
  }, [initialEmptyMiles, initialSource, initialAutoCalculate]);

  useEffect(() => {
    setProvider(!enablePcMiler ? PROVIDER.GOOGLE : routingSettings?.provider ?? PROVIDER.GOOGLE);
  }, [enablePcMiler, routingSettings?.provider]);

  useEffect(() => {
    if (initialAutoCalculate) {
      setIsAutoCalculate(true);
    }
  }, [initialAutoCalculate]);

  useEffect(() => {
    setEmptyMiles(initialEmptyMiles === null ? undefined : initialEmptyMiles);
  }, [initialEmptyMiles]);

  useEffect(() => {
    if (isAutoCalculate && openCalculateEmptyMiles) {
      fetchLocations();
    }
  }, [openCalculateEmptyMiles, isAutoCalculate]);

  useEffect(() => {
    const locations = [...twLocations];
    const newLocations: TwAutocompleteOption[] = locations.map((l) => ({
      id: l.id,
      label: l.name || '',
      subLabel: l.full_address
        ? l.full_address
        : l.address_1 && l.address_2
        ? `${l.address_1}, ${l.address_2}`
        : l.address_1,
      value: l || ''
    }));

    setLocations(newLocations);
  }, [JSON.stringify(twLocations.map((loc) => loc.id))]);

  useEffect(() => {
    if (isAutoCalculate && openCalculateEmptyMiles) {
      if (lastKnownLocation) {
        getEmptyMiles();
      } else {
        getLastLocation();
      }
    }
  }, [lastKnownLocation, openCalculateEmptyMiles, isAutoCalculate, getEmptyMiles, getLastLocation]);

  useEffect(() => {
    if (openCalculateEmptyMiles && emptyMiles !== undefined) {
      setTempEmptyMiles(Number(emptyMiles.toFixed(2)));
    }
  }, [openCalculateEmptyMiles, emptyMiles]);

  const handleOpenCalculatorModal = (): void => {
    if (!truck) {
      addNotification({
        message: 'Truck is required to calculate empty miles',
        variant: 'error'
      });
      return;
    }

    if (!firstStop?.provider_location?.id) {
      addNotification({
        message: 'First stop location is required to calculate empty miles',
        variant: 'error'
      });
      return;
    }

    setOpenCalculateEmptyMiles(true);
  };

  const handleCloseCalculatorModal = (): void => {
    setOpenCalculateEmptyMiles(false);
    setLastKnownLocation(null);
  };

  const handleSaveEmptyMiles = (): void => {
    setTooltip(getTooltipText('Empty miles', provider, isAutoCalculate));

    if (enableLtl && entity === EntityType.TRIP) {
      setOpenCalculateEmptyMiles(false);
      setLastKnownLocation(null);
      dispatch(
        updateTripEmptyMiles({
          value: tempEmptyMiles === undefined ? null : Number(tempEmptyMiles.toFixed(2)),
          is_auto: isAutoCalculate,
          source: provider
        })
      );
    }
    if (!enableLtl && entity === EntityType.ORDER && segmentUid) {
      dispatch(
        updateEmptyMilesSegment({
          segmentUid: segmentUid,
          value: tempEmptyMiles === undefined ? null : Number(tempEmptyMiles.toFixed(2)),
          is_auto: isAutoCalculate,
          source: provider
        })
      );
      setOpenCalculateEmptyMiles(false);
      setLastKnownLocation(null);
    }
  };

  const resetToOriginal = (): void => {
    setLastKnownLocation(originalLocation);
    setIsAutoCalculate(true);
  };

  const handleCreateEditLocation = (newLocation: LoadLocation): void => {
    const newAutocompleteOption: TwAutocompleteOption = {
      id: newLocation.id,
      label: newLocation.name,
      value: newLocation
    };

    const newLocations = {
      id: newLocation.id,
      label: newLocation.name || '',
      subLabel: newLocation.full_address
        ? newLocation.full_address
        : newLocation.address_1 && newLocation.address_2
        ? `${newLocation.address_1}, ${newLocation.address_2}`
        : newLocation.address_1,
      value: newLocation || ''
    };

    fetchLocations();

    setTimeout(() => {
      setLocations((prevLocations) => [newLocations, ...prevLocations]);
      setLastKnownLocation(newAutocompleteOption);
    }, 500);
  };

  const handleCreateLocationClose = (): void => {
    setIsCreateLocationOpen(false);
  };

  const handleEditLocationClose = (): void => {
    setIsEditLocationOpen(false);
  };

  return (
    <>
      <Stack>
        <StyledTextfieldLabel style={{ display: 'flex', alignItems: 'center' }}>
          Empty miles
          {emptyMiles && enablePcMiler ? (
            <TwTooltip title={tooltip} placement="top">
              <InfoOutlinedIcon sx={{ height: '16px' }} />
            </TwTooltip>
          ) : null}
        </StyledTextfieldLabel>
        {emptyMiles === undefined && (
          <TwButton
            type={TwButtonType.secondary}
            label="Calculate"
            action={handleOpenCalculatorModal}
          />
        )}

        {emptyMiles !== undefined && (
          <TwField
            inputType={TwInputTypes.number}
            name="empty-miles"
            endAdorment={
              <EditIcon
                sx={{ position: 'relative', left: '5px', cursor: 'pointer' }}
                onClick={handleOpenCalculatorModal}
              />
            }
            value={emptyMiles.toFixed(2)}
            disabled
          />
        )}
      </Stack>

      <TwDialog
        open={openCalculateEmptyMiles}
        onClickClose={handleCloseCalculatorModal}
        title="Calculate empty miles"
        classNameActions="empty-miles__actions"
        actions={[
          <TwButton
            key="1"
            type={TwButtonType.secondary}
            label="Cancel"
            action={handleCloseCalculatorModal}
          />,
          <TwButton
            key="2"
            type={TwButtonType.primary}
            label="Save"
            action={handleSaveEmptyMiles}
          />
        ]}
      >
        <Stack className="empty-miles__options-container">
          <Stack className="empty-miles__options" flexDirection={'row'}>
            <TwRadioButton
              value={'manually'}
              checked={!isAutoCalculate}
              name="calculate-manually"
              label=""
              onInputChange={(): void => {
                setIsAutoCalculate(false);
              }}
            />
            <Box>
              <Typography fontWeight={'500'} fontSize={'14px'}>
                Enter miles manually
              </Typography>
              <Typography fontSize={'14px'} color={'#6B7280'}>
                Add the milage by yourself
              </Typography>
            </Box>
          </Stack>

          {!isAutoCalculateMileageDisabled && (
            <Stack className="empty-miles__options" flexDirection={'row'}>
              <TwRadioButton
                value={'automatically'}
                checked={isAutoCalculate}
                name="calculate-automatically"
                label=""
                onInputChange={(): void => {
                  setIsAutoCalculate(true);
                }}
              />
              <Box>
                <Typography fontWeight={'500'} fontSize={'14px'}>
                  Calculate automatically
                </Typography>
                <Typography fontSize={'14px'} color={'#6B7280'}>
                  Provide addresses to make a calculation
                </Typography>
              </Box>
            </Stack>
          )}
        </Stack>

        {!isAutoCalculate ? (
          <TwField
            inputType={TwInputTypes.number}
            name="empty-miles-calculation"
            label="Empty milage"
            value={tempEmptyMiles}
            onInputChange={(e): void => {
              if (e.target.value) {
                setTempEmptyMiles(Number(e.target.value));
              } else {
                setTempEmptyMiles(undefined);
              }
            }}
            sx={{ width: '180px' }}
            onKeyDown={handleKeyDownCharacters}
          />
        ) : (
          <>
            <div className="stop__location-item">
              <TwAutocomplete
                name="last-known-location"
                className="w-144 stop__location stop__location--no-right-border"
                label="Last known location"
                optionList={locations}
                filterOptions={(x: any): any => x}
                value={lastKnownLocation}
                onInputChange={(e, value): void => {
                  // only trigger fetch if the user is typing
                  if (value && e && e.type === 'change') {
                    fetchLocations(value);
                  }
                }}
                onChange={(_, newValue: TwAutocompleteOption | string | null): void => {
                  setLastKnownLocation(newValue as TwAutocompleteOption);
                }}
                renderOption={(props, option): React.ReactNode => {
                  return <RenderOption props={props} option={option} />;
                }}
                listAction={{
                  label: 'New location',
                  action: (): void => {
                    setIsCreateLocationOpen(true);
                  }
                }}
                disableNoOption
                disableClearable
              />

              <TwLocationsDotsMenu
                menuOptions={[
                  {
                    label: 'Create new location',
                    action: (): void => {
                      setIsCreateLocationOpen(true);
                    },
                    disabled: false
                  },
                  {
                    label: 'Edit saved location',
                    action: (): void => {
                      lastKnownLocation && setIsEditLocationOpen(true);
                    },
                    disabled: !lastKnownLocation
                  },
                  {
                    label: 'View on Google Maps',
                    action: (): void => {
                      lastKnownLocation &&
                        window.open(
                          getMapsSearchUrl(
                            lastKnownLocation.value as LoadLocation,
                            enableAddressLocationLink
                          )
                        );
                    },
                    disabled: !lastKnownLocation,
                    icon: (
                      <OpenInNewIcon
                        sx={{ color: '#9CA3AF', fontSize: '14px', marginLeft: '5px' }}
                      />
                    )
                  }
                ]}
              />
            </div>
            <TwButton
              type={TwButtonType.text}
              label="Reset to original location"
              action={resetToOriginal}
              className="empty-miles__reset-button"
            />

            <StyledTextfieldLabel>Pickup location</StyledTextfieldLabel>
            <Box className="empty-miles__result-container">
              <StyledTextfieldLabel>
                {firstStop && firstStop.provider_location?.label}
              </StyledTextfieldLabel>
            </Box>

            <Stack
              flexDirection={'row'}
              justifyContent={'space-between'}
              className="empty-miles__result-container"
            >
              <Box>
                <StyledTextfieldLabel>Empty miles</StyledTextfieldLabel>
              </Box>
              <Typography fontWeight={'600'}>
                {tempEmptyMiles && `${tempEmptyMiles.toFixed(2)} mi`}
              </Typography>
            </Stack>
          </>
        )}
      </TwDialog>

      <CreateLocation
        open={isCreateLocationOpen}
        onClose={handleCreateLocationClose}
        onSuccess={handleCreateEditLocation}
      />

      <EditLocation
        locationId={lastKnownLocation?.id || ''}
        open={isEditLocationOpen}
        onClose={handleEditLocationClose}
        onSuccess={handleCreateEditLocation}
      />
    </>
  );
};

export default TwEmptyMiles;
